import React, {useState} from 'react';
import {DefaultButton, IconButton, Label, Panel, PanelType, Spinner, SpinnerSize, Text} from '@fluentui/react';
import {IStorefrontSettings} from '../../models/Storefront';
import {OpportunityTokenMode, useOpportunityTokens} from './useOpportunityTokens';

// Lists every token available on a storefront opportunity override page so an admin can see which {{{token}}} to
// paste into the fields. Token contexts are documented on useOpportunityTokens; this panel just renders the two
// groups (general + per-question) it returns. Fields also offer these same tokens as inline autosuggest.
interface IStorefrontOpportunityTokenReferenceProps {
    settings: IStorefrontSettings;
    mode: OpportunityTokenMode;
}

const StorefrontOpportunityTokenReference: React.FC<IStorefrontOpportunityTokenReferenceProps> = ({settings, mode}) => {
    const {generalTokens, questionEntries, generalLoading, questionsLoading} = useOpportunityTokens(settings, mode);

    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState<string | undefined>();
    const copy = (token: string) => {
        navigator.clipboard?.writeText(token);
        setCopied(token);
        window.setTimeout(() => setCopied(current => (current === token ? undefined : current)), 1500);
    };

    return (
        <>
            <DefaultButton iconProps={{iconName: 'Tag'}} text="Available Tokens"
                           styles={{root: {marginBottom: 8}}} onClick={() => setIsOpen(true)} />
            <Panel isOpen={isOpen} onDismiss={() => setIsOpen(false)} type={PanelType.medium}
                   headerText="Available Tokens" closeButtonAriaLabel="Close"
                   isBlocking={false} isLightDismiss={false}>
                <Text variant="small" block style={{color: '#605e5c', marginBottom: 8}}>
                    Paste a token into any field; it is replaced with the corresponding value when the opportunity
                    is created. Fields that accept tokens also suggest them as you type an opening brace.
                </Text>

                <Label style={{marginBottom: 4}}>General</Label>
                {generalLoading && <Spinner size={SpinnerSize.small} label="Loading tokens…" labelPosition="right" />}
                {!generalLoading && generalTokens.map(t => (
                    <TokenRow key={t.key}
                              token={t.token}
                              description={t.description}
                              copied={copied === t.token}
                              onCopy={() => copy(t.token)} />
                ))}

                <Label style={{marginTop: 16, marginBottom: 4}}>Storefront Questions</Label>
                {questionsLoading && <Spinner size={SpinnerSize.small} label="Loading questions…" labelPosition="right" />}
                {!questionsLoading && questionEntries.length === 0 &&
                    <p style={{color: '#605e5c', marginTop: 4}}>No questions are configured for this storefront.</p>}
                {!questionsLoading && questionEntries.map(entry => (
                    <div key={entry.key} style={{borderLeft: '3px solid #edebe9', paddingLeft: 10, marginBottom: 10}}>
                        <Label style={{marginBottom: 2}}>{entry.label}</Label>
                        <TokenRow token={entry.answerToken} description="Answer"
                                  copied={copied === entry.answerToken} onCopy={() => copy(entry.answerToken)} />
                        <TokenRow token={entry.questionToken} description="Question text"
                                  copied={copied === entry.questionToken} onCopy={() => copy(entry.questionToken)} />
                    </div>
                ))}
            </Panel>
        </>
    );
};

const TokenRow: React.FC<{token: string; description: string; copied: boolean; onCopy: () => void}> =
    ({token, description, copied, onCopy}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 6, marginTop: 3}}>
        <IconButton iconProps={{iconName: copied ? 'CheckMark' : 'Copy'}} title="Copy token" ariaLabel="Copy token"
                    styles={{root: {height: 24, width: 24}, icon: {fontSize: 12}}} onClick={onCopy} />
        <code style={{background: '#f3f2f1', padding: '2px 6px', borderRadius: 3, fontSize: 12, whiteSpace: 'nowrap'}}>{token}</code>
        <span style={{color: '#605e5c', fontSize: 12}}>{description}</span>
    </div>
);

export default StorefrontOpportunityTokenReference;
